import Image from 'next/image';
import type { LocationCardConfig } from '../location-card/LocationCardComponent';
import { SEAL_ALT_FALLBACK, type HeroResolved } from './types';
import { imageAlt } from '@/lib/render/imageAlt';
import { useRenderContext } from '@/lib/render/RenderContextProvider';
import { resolveMediaPlaceholder } from '@/lib/render/resolveMediaPlaceholder';
import { Text } from '@/lib/render/Text';

export function HeroComponentB({ resolved }: { resolved: HeroResolved }) {
  const {
    substitutePlaceholders: sub,
    entities,
    imageUrl,
  } = useRenderContext();
  const { title, backgroundImage, content = [], seal } = resolved;
  // `backgroundOverride` holds an entity-image token like {city.image}:
  // when it resolves, that image beats the block's own backgroundImage —
  // an entity without the referenced image falls back to backgroundImage.
  const background =
    resolveMediaPlaceholder(resolved.backgroundOverride, entities) ??
    backgroundImage;
  const bgUrl = imageUrl(background);
  // A hero photo is a backdrop and stays unlabelled while the media carries no
  // alt text. Once an editor types one in Strapi the picture is content, so it
  // gets its accessible name — substituted, like every editor-typed string.
  const bgAlt = sub(imageAlt(background));
  // Shown whenever an editor uploaded one, and deliberately *not* gated on
  // `loc.isoCertified` — this slot held the hardcoded TÜV badge before SH-411.
  const sealUrl = seal ? imageUrl(seal) : '';
  // Editors supply the seal's alt on the Strapi media; SEAL_ALT_FALLBACK
  // covers an empty one, and a placeholder in it is substituted like any
  // other editor-typed string.
  const sealAlt = sub(imageAlt(seal, SEAL_ALT_FALLBACK));

  // Campaign variants read `content` only for the Location Card's two text
  // fields. A Hero Card in the zone is ignored here by design (SH-312 Q6) —
  // the cards render in variant A only.
  const cardBlock = content.find(
    (b): b is LocationCardConfig => b.componentType === 'widgets.location-card',
  );
  const locationTitle = cardBlock ? sub(cardBlock.locationTitle) : undefined;
  const promoText = cardBlock ? sub(cardBlock.promoText) : undefined;
  const [promoHeadline, promoSubtext] = promoText?.split('\n') ?? [];

  return (
    <section className="relative z-0 -mx-4 -mb-16 w-[calc(100%+2rem)] overflow-hidden lg:mx-0 lg:mb-0 lg:w-full lg:rounded-2xl">
      <div className="relative aspect-839/774 w-full">
        {bgUrl && (
          <Image
            src={bgUrl}
            alt={bgAlt}
            fill
            className="object-cover lg:rounded-2xl"
            priority
            loading="eager"
            fetchPriority="high"
          />
        )}

        {/* Scrim for the headline block below (SH-444). The hero carries no
            top gradient: the header sits on its own background. */}
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-linear-to-t from-black/80 to-transparent lg:rounded-b-2xl" />

        {/* Location tag + headline + subline */}
        <div className="absolute right-[110px] bottom-12 left-8 z-10 lg:right-auto lg:bottom-16 lg:left-12">
          {locationTitle && (
            <span className="text-primary mb-4 inline-block rounded-full bg-white px-2.5 py-1.5 text-[10px] font-semibold tracking-wide uppercase lg:px-6 lg:py-2 lg:text-sm">
              {locationTitle}
            </span>
          )}
          {title && (
            <Text
              as="h1"
              field="title"
              className="block max-w-[90%] text-2xl leading-tight font-bold text-white lg:text-5xl lg:leading-tight lg:font-semibold"
            >
              {title}
            </Text>
          )}
          {promoHeadline && (
            <p className="mt-3 text-base font-semibold text-white lg:text-lg lg:font-normal">
              {promoHeadline}
              {promoSubtext && (
                <>
                  <br />
                  {promoSubtext}
                </>
              )}
            </p>
          )}
        </div>

        {/* CMS-managed seal — same slot the TÜV badge used (SH-411) */}
        {sealUrl && (
          <div className="absolute right-4 bottom-12 z-10 lg:top-6 lg:right-6 lg:bottom-auto">
            <Image
              src={sealUrl}
              alt={sealAlt}
              width={120}
              height={160}
              className="h-[110px] w-[83px] drop-shadow-lg lg:h-[160px] lg:w-[120px]"
            />
          </div>
        )}
      </div>
    </section>
  );
}
